import type { ThemeConfig } from 'antd';
import { borderRadius } from './border-radius';
import { colors } from './colors';
import { shadows } from './shadows';
import { spacing } from './spacing';
import { typography } from './typography';

/**
 * Convert rem string to pixel number
 * Assumes 1rem = 16px (standard browser default)
 */
const remToPx = (remValue: string): number => {
  // Handle special cases like '0' or '9999px'
  if (remValue === '0' || remValue === '0px') return 0;
  if (remValue.endsWith('px')) {
    return parseFloat(remValue);
  }
  // Extract numeric value from rem string (e.g., '0.5rem' -> 0.5)
  const rem = parseFloat(remValue);
  return rem * 16;
};

/**
 * Comprehensive Ant Design theme configuration
 * Covers all components used in the application
 */
export const antdTheme: ThemeConfig = {
  token: {
    // Color tokens
    colorPrimary: colors.primary,
    colorSuccess: colors.success.main,
    colorWarning: colors.warning.main,
    colorError: colors.error.main,
    colorInfo: colors.info.main,

    // Typography tokens
    fontFamily: typography.fontFamily.sans.join(', '),
    fontSize: remToPx(typography.fontSize.base), // 1rem = 16px
    fontSizeHeading1: remToPx(typography.fontSize['4xl']), // 2.25rem = 36px
    fontSizeHeading2: remToPx(typography.fontSize['3xl']), // 1.875rem = 30px
    fontSizeHeading3: remToPx(typography.fontSize['2xl']), // 1.5rem = 24px
    fontSizeHeading4: remToPx(typography.fontSize.xl), // 1.25rem = 20px
    fontSizeHeading5: remToPx(typography.fontSize.lg), // 1.125rem = 18px
    fontSizeLG: remToPx(typography.fontSize.lg), // 1.125rem = 18px
    fontSizeSM: remToPx(typography.fontSize.sm), // 0.875rem = 14px
    fontSizeXS: remToPx(typography.fontSize.xs), // 0.75rem = 12px

    // Border radius (convert rem to px: 1rem = 16px)
    borderRadius: remToPx(borderRadius.md), // 0.375rem = 6px
    borderRadiusLG: remToPx(borderRadius.lg), // 0.5rem = 8px
    borderRadiusSM: remToPx(borderRadius.sm), // 0.125rem = 2px

    // Spacing
    padding: remToPx(spacing[4]), // 1rem = 16px
    paddingLG: remToPx(spacing[6]), // 1.5rem = 24px
    paddingSM: remToPx(spacing[3]), // 0.75rem = 12px
    paddingXS: remToPx(spacing[2]), // 0.5rem = 8px
    margin: remToPx(spacing[4]), // 1rem = 16px
    marginLG: remToPx(spacing[6]), // 1.5rem = 24px
    marginSM: remToPx(spacing[3]), // 0.75rem = 12px
    marginXS: remToPx(spacing[2]), // 0.5rem = 8px

    // Control height
    controlHeight: 40,
    controlHeightLG: 48,
    controlHeightSM: 32,

    // Line width
    lineWidth: 1,
    lineType: 'solid',

    // Motion (in milliseconds as strings)
    motionDurationFast: '150ms',
    motionDurationMid: '200ms',
    motionDurationSlow: '300ms',

    // Z-index
    zIndexBase: 0,
    zIndexPopupBase: 1000,
  },
  components: {
    Typography: {
      colorTextHeading: colors.blue[800],
      fontWeightStrong: typography.fontWeight.bold,
      padding: 0,
      margin: 0,
      titleMarginBottom: '0.5em',
      titleMarginTop: '1.2em',
    },

    // Button
    Button: {
      primaryColor: '#ffffff',
      defaultBg: '#ffffff',
      defaultBorderColor: colors.gray[300],
      defaultColor: colors.gray[800],
      borderRadius: remToPx(borderRadius.component.button), // 0.375rem = 6px
      fontWeight: typography.fontWeight.medium,
      controlHeight: 40,
      paddingInline: remToPx(spacing[6]), // 1.5rem = 24px
      paddingBlock: remToPx(spacing[2]), // 0.5rem = 8px
      fontSize: remToPx(typography.fontSize.sm), // 0.875rem = 14px
      boxShadow: shadows.none,
      // Primary button (orange)
      colorPrimary: colors.primary,
      colorPrimaryHover: colors.orange[500],
      colorPrimaryActive: colors.orange[500],
      // Secondary button (blue)
      colorLink: colors.secondary,
      colorLinkHover: colors.blue[700],
    },

    // Table
    Table: {
      headerBg: colors.gray.lighter,
      headerColor: colors.blue[800],
      borderColor: colors.gray[300],
      borderRadius: 0,
      headerBorderRadius: 0,
      cellPaddingBlock: remToPx(spacing[4]), // 1rem = 16px
      cellPaddingInline: remToPx(spacing[4]), // 1rem = 16px
      fontSize: remToPx(typography.fontSize.sm), // 0.875rem = 14px
      fontWeightStrong: typography.fontWeight.semibold,
    },

    // Input
    Input: {
      borderRadius: remToPx(borderRadius.component.input), // 0.375rem = 6px
      controlHeight: 40,
      paddingInline: remToPx(spacing[3]), // 0.75rem = 12px
      paddingBlock: remToPx(spacing[2]), // 0.5rem = 8px
      colorBorder: colors.gray[300],
      colorBorderHover: colors.blue[400],
      colorPrimaryHover: colors.blue[400],
      activeBorderColor: colors.blue[500],
      hoverBorderColor: colors.blue[400],
    },

    // Form
    Form: {
      labelColor: colors.blue[800],
      labelFontSize: remToPx(typography.fontSize.sm), // 0.875rem = 14px
      labelHeight: remToPx(spacing[6]), // 1.5rem = 24px
      itemMarginBottom: remToPx(spacing[6]), // 1.5rem = 24px
      verticalLabelPadding: `0 0 ${remToPx(spacing[2])}px`, // 0 0 8px
      verticalLabelMargin: 0,
    },

    // DatePicker
    DatePicker: {
      borderRadius: remToPx(borderRadius.component.input), // 0.375rem = 6px
      controlHeight: 40,
      paddingInline: remToPx(spacing[3]), // 0.75rem = 12px
      colorBorder: colors.gray[300],
      hoverBorderColor: colors.blue[400],
      activeBorderColor: colors.blue[500],
    },

    // Select
    Select: {
      borderRadius: remToPx(borderRadius.component.input), // 0.375rem = 6px
      controlHeight: 40,
      colorBorder: colors.gray[300],
      hoverBorderColor: colors.blue[400],
      activeBorderColor: colors.blue[500],
    },

    // Drawer
    Drawer: {
      paddingLG: remToPx(spacing[6]), // 1.5rem = 24px
      padding: remToPx(spacing[4]), // 1rem = 16px
      colorBgElevated: '#ffffff',
      borderRadius: remToPx(borderRadius.component.modal), // 0.75rem = 12px
    },

    // Modal
    Modal: {
      borderRadius: remToPx(borderRadius.component.modal), // 0.75rem = 12px
      paddingContentHorizontal: remToPx(spacing[6]), // 1.5rem = 24px
      paddingContentVertical: 20, // Custom value, not in spacing scale
      titleFontSize: remToPx(typography.fontSize['2xl']), // 1.5rem = 24px
      titleFontWeight: typography.fontWeight.bold,
    },

    // Dropdown
    Dropdown: {
      borderRadius: remToPx(borderRadius.md), // 0.375rem = 6px
      paddingXXS: remToPx(spacing[1]), // 0.25rem = 4px
      paddingXS: remToPx(spacing[2]), // 0.5rem = 8px
      controlItemBgHover: colors.gray.lighter,
      controlItemBgActive: colors.gray['lighter-active'],
    },

    // Skeleton
    Skeleton: {
      borderRadius: remToPx(borderRadius.md), // 0.375rem = 6px
      colorFill: colors.gray.lighter,
      colorFillContent: colors.gray[300],
    },

    // Empty
    Empty: {
      colorTextDisabled: colors.gray[600],
      fontSize: remToPx(typography.fontSize.base), // 1rem = 16px
    },

    // Tag
    Tag: {
      borderRadius: remToPx(borderRadius.md), // 0.375rem = 6px
      fontSize: remToPx(typography.fontSize.sm), // 0.875rem = 14px
      lineHeight: typography.lineHeight.normal,
    },

    // Divider
    Divider: {
      colorSplit: colors.gray[300],
      margin: remToPx(spacing[4]), // 1rem = 16px
    },

    // Flex
    Flex: {
      gap: remToPx(spacing[4]), // 1rem = 16px
    },

    // Switch
    Switch: {
      colorPrimary: colors.primary,
      colorPrimaryHover: colors.orange[500],
    },

    // Notification
    Notification: {
      borderRadius: remToPx(borderRadius.lg), // 0.5rem = 8px (fixed from parseFloat)
      paddingContent: remToPx(spacing[4]), // 1rem = 16px
      padding: remToPx(spacing[4]), // 1rem = 16px
      margin: remToPx(spacing[4]), // 1rem = 16px
    },
  },
} as ThemeConfig;
